import React, { useEffect, useState } from 'react';
import { getTrialInfo } from '../../services/api';

/**
 * Banner de alerta de quotas (Fase 2).
 *
 * Exibe um banner compacto quando o tenant atinge ≥85% de alguma quota
 * do seu plano. Hierarquia de cor:
 *   - 100% (estourado)  → vermelho
 *   - 85-99%            → laranja
 *
 * Mostra no máximo as 2 quotas mais críticas para evitar poluição.
 * Dismissível por sessão (sessionStorage) — reaparece a cada novo login
 * ou refresh completo do navegador.
 *
 * Quotas ilimitadas (`unlimited: true`) nunca aparecem aqui.
 *
 * Uso: montado dentro do TenantLayout, logo acima do <Outlet />.
 */

const QUOTA_LABELS = {
  users:        { icon: '👥', label: 'usuários',       unit: '' },
  appointments: { icon: '📅', label: 'agendamentos',   unit: '/mês' },
  services:     { icon: '🛎️', label: 'serviços',       unit: '' },
  whatsapp:     { icon: '💬', label: 'msgs. WhatsApp', unit: '/mês' },
};

const WARN_THRESHOLD = 85;
const DISMISS_KEY = 'atendoapp:quota_banner_dismissed';

export default function QuotaBanner() {
  const [quotas, setQuotas] = useState(null);
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem(DISMISS_KEY) === '1'
  );

  useEffect(() => {
    // A rota /tenant/trial-info retorna { ok: true, trial_info: {...} } —
    // as quotas estao aninhadas em trial_info.quotas. Acessar r.data.quotas
    // (top-level) era o bug silencioso que impedia este banner de aparecer.
    getTrialInfo()
      .then(r => setQuotas(r.data?.trial_info?.quotas || null))
      .catch(() => {});
  }, []);

  if (dismissed || !quotas) return null;

  // Ordena por pct desc e pega as que passaram do threshold, máximo 2
  const critical = Object.entries(QUOTA_LABELS)
    .map(([key, meta]) => ({ key, meta, ...(quotas[key] || {}) }))
    .filter(q => !q.unlimited && q.pct >= WARN_THRESHOLD)
    .sort((a, b) => b.pct - a.pct)
    .slice(0, 2);

  if (critical.length === 0) return null;

  const hasBlocked = critical.some(q => q.pct >= 100);
  const bg = hasBlocked ? '#fef2f2' : '#fff7ed';
  const border = hasBlocked ? '#fca5a5' : '#fdba74';
  const textColor = hasBlocked ? '#991b1b' : '#9a3412';
  const iconText = hasBlocked ? '🚫' : '⚠️';
  const title = hasBlocked
    ? 'Limite do plano atingido'
    : 'Você está próximo do limite do plano';

  const handleDismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  };

  return (
    <div
      className={`quota-banner quota-banner--${hasBlocked ? 'danger' : 'warning'}`}
      style={{
        background: bg,
        border: `1px solid ${border}`,
        borderLeft: `4px solid ${border}`,
        borderRadius: 8,
        padding: '12px 16px',
        margin: '12px 16px 0',
        color: textColor,
        fontSize: 13,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        flexWrap: 'wrap',
      }}
      role="alert"
    >
      <div className="quota-banner__icon" style={{ fontSize: 20, lineHeight: 1, flexShrink: 0 }}>{iconText}</div>
      <div className="quota-banner__body" style={{ flex: 1, minWidth: 200 }}>
        <div className="quota-banner__title" style={{ fontWeight: 700, marginBottom: 4 }}>{title}</div>
        <div className="quota-banner__message" style={{ lineHeight: 1.5 }}>
          {critical.map((q, i) => (
            <span key={q.key}>
              {i > 0 && ' · '}
              {q.meta.icon} <strong>{q.used}/{q.limit}</strong> {q.meta.label}{q.meta.unit}
              {' '}
              <span style={{ opacity: 0.8 }}>({q.pct}%)</span>
            </span>
          ))}
        </div>
        <div style={{ marginTop: 6, fontSize: 12, opacity: 0.9 }}>
          {hasBlocked
            ? 'Novas operações nesta cota estão bloqueadas. Entre em contato para fazer upgrade do plano.'
            : 'Considere fazer upgrade do plano antes de atingir o limite.'
          }
        </div>
      </div>
      <button
        onClick={handleDismiss}
        style={{
          background: 'transparent',
          border: 'none',
          color: textColor,
          cursor: 'pointer',
          fontSize: 18,
          opacity: 0.7,
          padding: 4,
          lineHeight: 1,
          flexShrink: 0,
        }}
        aria-label="Dispensar alerta"
        title="Dispensar alerta"
      >
        ✕
      </button>
    </div>
  );
}
